import { cn } from '@/client/utils';
import { formatRate } from '@/client/utils/format';
import { Root, Track, Range, Thumb } from '@radix-ui/react-slider';
import { useRef } from 'react';
import useHoverDirty from 'react-use/lib/useHoverDirty';

type SliderProps = {
  className?: string;
  value: number;
  step?: number;
  min?: number;
  max?: number;
  disabled?: boolean;
  setValue: (value: number) => void;
};
export const Slider = ({ className, value = 0, setValue, step = 1, min = 0, max = 100, ...attrs }: SliderProps) => {
  const dotLength = 5;
  const ref = useRef<HTMLSpanElement>(null);
  const ref2 = useRef<HTMLSpanElement>(null);
  const isHover = useHoverDirty(ref);
  const isHoverThumb = useHoverDirty(ref2);
  return (
    <div className={cn('flex w-full items-center relative', className)}>
      <Root
        {...attrs}
        ref={ref}
        className="relative flex w-full touch-none select-none items-center"
        step={step}
        min={min}
        max={max}
        value={[value]}
        onValueChange={([input]) => {
          setValue(input);
        }}
      >
        <Track className="relative h-.5 w-full grow overflow-hidden rounded-full bg-[#D0D0D0]">
          <Range className="absolute h-full bg-primary" />
        </Track>
        <div className="flex items-center justify-between pos-absolute w-full">
          {Array.from({ length: dotLength }).map((_, i) => (
            <span
              key={`slider-dot-${i}`}
              className={cn(
                'rounded-full bg-[#D0D0D0] hover:bg-primary cursor-pointer',
                value >= i * (max / dotLength) && 'bg-primary',
                isHover || value > 0 ? 'size-2' : 'size-1'
              )}
              onClick={e => {
                if (isHoverThumb) return;
                e.preventDefault();
                const value = i * (max / (dotLength - 1));
                setValue(dotLength - 1 === i ? max : Number(value));
              }}
            ></span>
          ))}
        </div>
        {(isHover || value > 0) && (
          <Thumb
            ref={ref2}
            className="block pos-relative cursor-pointer relative size-4 rounded-full border  group border-foreground/40 bg-card shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-transparent disabled:pointer-events-none disabled:opacity-50"
          >
            {isHover && (
              <div className="pos-absolute group-hover:block -top-[calc(100%+1.2rem)] left-0 -translate-x-[calc(50%-.5rem)] z-50 bg-black py-1 px-2 text-sm text-white rounded-md">
                {max && value ? formatRate(Math.min(Math.round(value ? (value * 1e2) / max : 0), 100)) : '0%'}
              </div>
            )}
          </Thumb>
        )}
      </Root>
    </div>
  );
};
